import * as React from 'react';
import * as classnames from 'classnames';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { readReviewsTheme, reviewsTextTheme } from '../theme/default-theme';
import { Caption } from 'elc-typography';
import { ContentSpacing } from 'elc-base-theme';

export interface IReadReviews {
    className?: string;
    readReviewsLabel?: string;
    reviewsNumber: number;
    firstToWriteReviewLabel?: string;
    onReadReviewsClick?(): void;
    onWriteReviewsClick?(): void;
    productLink?: string;
    hideReviewsLabel?: boolean;
}

export interface IReviewsLink {
    label?: string;
    className?: string;
    onClick?(): void;
}

const ReadReviewsWrapper = styled.div`
    display: inline-block;
    vertical-align: middle;
    padding: 2px ${ContentSpacing.space12};
    margin: 0 ${ContentSpacing.space8};
    border-left-width: 1px;
    border-left-style: solid;
    ${readReviewsTheme};
    [dir='rtl'] & {
        border-right: 1px solid;
        border-left: none;
    }
`;

const ReadBtn = styled.a.attrs({
    role: 'button',
    tabIndex: 0
})`
    cursor: pointer;
    padding: 2px 4px;
`;

const StyledText = styled(Caption)`
    display: inline-block;
    text-decoration: underline;
    ${reviewsTextTheme};
`;

const readReviewsClassName = classnames('elc-read-reviews-link', 'js-read-reviews-link');
const writeReviewClassName = classnames('elc-write-first-review', 'js-write-first-review');

export const ReadReviews = observer((props: IReadReviews) => {
    const {
        reviewsNumber,
        firstToWriteReviewLabel,
        readReviewsLabel,
        className,
        productLink,
        onReadReviewsClick,
        onWriteReviewsClick,
        hideReviewsLabel
    } = props;
    const classNames = classnames('elc-read-reviews', 'js-read-reviews', className);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleReviewsEnterKeyPressed = (func: any) => (
        event: React.KeyboardEvent<HTMLAnchorElement>
    ) => {
        if (event.key === 'Enter' || event.key === ' ') {
            func();
        }
    };

    const ReviewsLink = (linkProps: IReviewsLink) => {
        const reviewsLinkClassNames = classnames(
            'elc-product-reviews-link',
            'js-product-reviews-link',
            linkProps.className
        );

        return (
            <a
                href={productLink}
                aria-label={firstToWriteReviewLabel}
                tabIndex={0}
                role="button"
                title={productLink}
                className={reviewsLinkClassNames}
                onClick={linkProps.onClick}
                data-test-id="product_reviews-link"
                onKeyPress={handleReviewsEnterKeyPressed(linkProps.onClick)}
            >
                {linkProps.label}
            </a>
        );
    };

    return (
        <ReadReviewsWrapper className={classNames}>
            {hideReviewsLabel ? (
                reviewsNumber === 0 ? (
                    <ReviewsLink
                        className={writeReviewClassName}
                        label={firstToWriteReviewLabel}
                        onClick={onWriteReviewsClick}
                    />
                ) : null
            ) : reviewsNumber > 0 ? (
                onReadReviewsClick ? (
                    <ReviewsLink
                        className={readReviewsClassName}
                        label={readReviewsLabel}
                        onClick={onReadReviewsClick}
                    />
                ) : (
                    <ReadBtn className={readReviewsClassName} aria-label={readReviewsLabel}>
                        <StyledText>{readReviewsLabel}</StyledText>
                    </ReadBtn>
                )
            ) : (
                onWriteReviewsClick && (
                    <ReviewsLink
                        className={writeReviewClassName}
                        label={firstToWriteReviewLabel}
                        onClick={onWriteReviewsClick}
                    />
                )
            )}
        </ReadReviewsWrapper>
    );
});
